import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'
import { useNavigate } from 'react-router-dom'

interface Tour {
  id: string
  name: string
  start_date: string
  end_date: string
  status: string
}

interface Flight {
  id: string
  flight_number: string
  departure_date: string
  arrival_date: string
  from_city: string
  to_city: string
}

interface Hotel {
  id: string
  name: string
  city: string
  check_in: string
  check_out: string
}

interface Transport {
  id: string
  type: string
  date: string
  from_location: string
  to_location: string
}

interface Show {
  id?: string
  _id?: string
  perf_date?: string
  date?: string
  artist?: string
  city?: string
  state?: string
  venue?: string
  event_title?: string
  status?: string
  current_ticket_count?: number | string
}

export default function TourManagerPage() {
  const navigate = useNavigate()
  const [shows, setShows] = useState<Show[]>([])
  const [tours, setTours] = useState<Tour[]>([])
  const [flights, setFlights] = useState<Flight[]>([])
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [transport, setTransport] = useState<Transport[]>([])
  const [_isLoading, setIsLoading] = useState(true)

  const [activeModal, setActiveModal] = useState<null | 'tour' | 'flight' | 'hotel' | 'transport'>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [tourForm, setTourForm] = useState({
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'upcoming',
  })
  const [flightForm, setFlightForm] = useState({
    flight_number: '',
    from_city: '',
    to_city: '',
    departure_date: new Date().toISOString().split('T')[0],
    arrival_date: new Date().toISOString().split('T')[0],
  })
  const [hotelForm, setHotelForm] = useState({
    name: '',
    city: '',
    check_in: new Date().toISOString().split('T')[0],
    check_out: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  })
  const [transportForm, setTransportForm] = useState({
    type: 'car',
    from_location: '',
    to_location: '',
    date: new Date().toISOString().split('T')[0],
  })

  const normalizeBookings = (payload: any) => {
    const list = payload?.data?.data || payload?.data || payload || []
    const array = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : []
    return array.map((item: any) => ({
      id: item._id || item.id,
      date: item.date || item.perf_date,
      perf_date: item.perf_date || item.date,
      event_title: item.event_title || item.title,
      venue: item.venue,
      city: item.city,
      state: item.state,
      artist: item.artist || item.artist_name,
      status: item.status,
    }))
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [showsData, toursData, flightsData, hotelsData, transportData] = await Promise.all([
          getDataAPI('user/getBookings').catch(() => []),
          getDataAPI('tours').catch(() => []),
          getDataAPI('flights').catch(() => []),
          getDataAPI('hotels').catch(() => []),
          getDataAPI('transport').catch(() => []),
        ])
        // Only show confirmed shows in Tour Manager
        const allShows = normalizeBookings(showsData)
        const confirmedShows = allShows.filter((show: Show) => show.status === 'confirmed')
        setShows(confirmedShows)
        setTours(toursData || [])
        setFlights(flightsData || [])
        setHotels(hotelsData || [])
        setTransport(transportData || [])
      } catch (error) {
        console.error('Error fetching tour data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const refresh = async () => {
    const [showsData, toursData, flightsData, hotelsData, transportData] = await Promise.all([
      getDataAPI('user/getBookings').catch(() => []),
      getDataAPI('tours').catch(() => []),
      getDataAPI('flights').catch(() => []),
      getDataAPI('hotels').catch(() => []),
      getDataAPI('transport').catch(() => []),
    ])
    // Only show confirmed shows in Tour Manager
    const allShows = normalizeBookings(showsData)
    const confirmedShows = allShows.filter((show: Show) => show.status === 'confirmed')
    setShows(confirmedShows)
    setTours(toursData || [])
    setFlights(flightsData || [])
    setHotels(hotelsData || [])
    setTransport(transportData || [])
  }

  const submit = async (kind: NonNullable<typeof activeModal>) => {
    setIsSubmitting(true)
    try {
      if (kind === 'tour') await postDataAPI('tours', tourForm)
      if (kind === 'flight') await postDataAPI('flights', flightForm)
      if (kind === 'hotel') await postDataAPI('hotels', hotelForm)
      if (kind === 'transport') await postDataAPI('transport', transportForm)
      setActiveModal(null)
      await refresh()
    } catch (error) {
      console.error(`Failed to create ${kind}:`, error)
      alert(`Failed to create ${kind}. Please try again.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10">
        <h1 className="text-2xl font-semibold tracking-tight mb-2">Tour Manager</h1>
        <p className="text-sm text-gray-500">Manage tours, flights, hotels, and transport</p>
      </div>

      {/* Shows (Confirmed bookings feed into Tour Manager) */}
      <section className="mb-10">
        <div className="mb-4 flex justify-between items-center">
          <div>
            <h2 className="text-sm font-medium text-gray-900">Confirmed Shows</h2>
            <p className="text-xs text-gray-500 mt-1">All confirmed shows from Shows page</p>
          </div>
          <Button onClick={() => navigate('/shows')}>Open Shows</Button>
        </div>
        {shows.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Artist
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Venue
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shows.map((show) => {
                  const key = show.id || show._id || `${show.artist}-${show.perf_date || show.date}`
                  const d = show.perf_date || show.date
                  return (
                    <tr key={key} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {d ? new Date(d).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{show.artist || '—'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {[show.city, show.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{show.venue || '—'}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                          {show.status || '—'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No shows found</p>
          </div>
        )}
      </section>

      {/* Tours */}
      <section className="mb-10">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-900">Tours</h2>
          <Button onClick={() => setActiveModal('tour')}>New Tour</Button>
        </div>
        {tours.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Start Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">End Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tours.map((tour) => (
                  <tr key={tour.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{tour.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(tour.start_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(tour.end_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                        {tour.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No tours found</p>
          </div>
        )}
      </section>

      {/* Flights */}
      <section className="mb-10">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-900">Flights</h2>
          <Button onClick={() => setActiveModal('flight')}>Add Flight</Button>
        </div>
        {flights.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Flight Number</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">From</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">To</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Departure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {flights.map((flight) => (
                  <tr key={flight.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{flight.flight_number}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{flight.from_city}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{flight.to_city}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(flight.departure_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No flights found</p>
          </div>
        )}
      </section>

      {/* Hotels */}
      <section className="mb-10">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-900">Hotels</h2>
          <Button onClick={() => setActiveModal('hotel')}>Add Hotel</Button>
        </div>
        {hotels.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Check In</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Check Out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hotels.map((hotel) => (
                  <tr key={hotel.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{hotel.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{hotel.city}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(hotel.check_in).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(hotel.check_out).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No hotels found</p>
          </div>
        )}
      </section>

      {/* Transport */}
      <section>
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-900">Transport</h2>
          <Button onClick={() => setActiveModal('transport')}>Add Transport</Button>
        </div>
        {transport.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">From</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">To</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transport.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{item.type}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{item.from_location}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{item.to_location}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(item.date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No transport found</p>
          </div>
        )}
      </section>

      <Modal
        isOpen={activeModal === 'tour'}
        onClose={() => setActiveModal(null)}
        title="Create Tour"
        maxWidthClassName="max-w-md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit('tour')
          }}
          className="space-y-5"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Tour Name</label>
            <input
              type="text"
              required
              value={tourForm.name}
              onChange={(e) => setTourForm({ ...tourForm, name: e.target.value })}
              className="ds-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                label="Start Date"
                value={tourForm.start_date}
                onChange={(value) => setTourForm({ ...tourForm, start_date: value })}
                required
              />
            </div>
            <div>
              <DatePicker
                label="End Date"
                value={tourForm.end_date}
                onChange={(value) => setTourForm({ ...tourForm, end_date: value })}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <input
              type="text"
              value={tourForm.status}
              onChange={(e) => setTourForm({ ...tourForm, status: e.target.value })}
              className="ds-input"
            />
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Creating…' : 'Create Tour'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={activeModal === 'flight'}
        onClose={() => setActiveModal(null)}
        title="Add Flight"
        maxWidthClassName="max-w-md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit('flight')
          }}
          className="space-y-5"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Flight Number</label>
            <input
              type="text"
              required
              value={flightForm.flight_number}
              onChange={(e) => setFlightForm({ ...flightForm, flight_number: e.target.value })}
              className="ds-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">From</label>
              <input
                type="text"
                required
                value={flightForm.from_city}
                onChange={(e) => setFlightForm({ ...flightForm, from_city: e.target.value })}
                className="ds-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">To</label>
              <input
                type="text"
                required
                value={flightForm.to_city}
                onChange={(e) => setFlightForm({ ...flightForm, to_city: e.target.value })}
                className="ds-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                label="Departure"
                value={flightForm.departure_date}
                onChange={(value) => setFlightForm({ ...flightForm, departure_date: value })}
                required
              />
            </div>
            <div>
              <DatePicker
                label="Arrival"
                value={flightForm.arrival_date}
                onChange={(value) => setFlightForm({ ...flightForm, arrival_date: value })}
                required
              />
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Saving…' : 'Add Flight'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={activeModal === 'hotel'}
        onClose={() => setActiveModal(null)}
        title="Add Hotel"
        maxWidthClassName="max-w-md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit('hotel')
          }}
          className="space-y-5"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Hotel Name</label>
            <input
              type="text"
              required
              value={hotelForm.name}
              onChange={(e) => setHotelForm({ ...hotelForm, name: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">City</label>
            <input
              type="text"
              required
              value={hotelForm.city}
              onChange={(e) => setHotelForm({ ...hotelForm, city: e.target.value })}
              className="ds-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                label="Check In"
                value={hotelForm.check_in}
                onChange={(value) => setHotelForm({ ...hotelForm, check_in: value })}
                required
              />
            </div>
            <div>
              <DatePicker
                label="Check Out"
                value={hotelForm.check_out}
                onChange={(value) => setHotelForm({ ...hotelForm, check_out: value })}
                required
              />
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Saving…' : 'Add Hotel'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={activeModal === 'transport'}
        onClose={() => setActiveModal(null)}
        title="Add Transport"
        maxWidthClassName="max-w-md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit('transport')
          }}
          className="space-y-5"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <input
              type="text"
              required
              value={transportForm.type}
              onChange={(e) => setTransportForm({ ...transportForm, type: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">From</label>
            <input
              type="text"
              required
              value={transportForm.from_location}
              onChange={(e) => setTransportForm({ ...transportForm, from_location: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">To</label>
            <input
              type="text"
              required
              value={transportForm.to_location}
              onChange={(e) => setTransportForm({ ...transportForm, to_location: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <DatePicker
              label="Date"
              value={transportForm.date}
              onChange={(value) => setTransportForm({ ...transportForm, date: value })}
              required
            />
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Saving…' : 'Add Transport'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
